/**
 * Onglet « Exercice en cours ».
 *
 * Toutes les valeurs proviennent de l'API. La colonne « réalisé » exprime des
 * flux de trésorerie : la mention figure explicitement, la comptabilité de
 * l'exercice étant tenue en encaissements et décaissements.
 */

import {
  A_CLASSER,
  A_CLASSER_RECETTES,
  ECART_RACCORDEMENT,
  dateFr,
  entier,
  euros,
  eurosCentimes,
  eurosSigne,
  keuros,
  nombre,
  pourcent,
  type DepensePrevue,
  type ExecutionPoste,
} from '@scc/core';
import {
  ajouterDepensePrevue,
  estimerResteAEngager,
  modifierDepensePrevue,
  supprimerDepensePrevue,
  type Charge,
} from '../api.js';
import { h, vider } from '../dom.js';
import { graphiqueAnneau } from '../graphiques.js';
import {
  badge,
  barre,
  barreExport,
  enteteSection,
  insight,
  kpi,
  montantTracable,
  noteLecture,
  regleAccent,
} from '../composants.js';
import { compteUnique, estALClasser, grouperLignesParPoste } from './groupes-poste.js';

/** Origine de la projection des cotisations, en clair (décision 41). */
function mentionCotisations(pr: Charge['projection']): string {
  const e = pr.estimationCotisations;
  if (pr.sourceCotisations === 'estimation' && e) {
    const fourchette = e.bas !== null && e.haut !== null ? ` — fourchette ${euros(e.bas)} à ${euros(e.haut)}` : '';
    return `Estimation compagnies.org du ${dateFr(e.calculeLe.slice(0, 10))}${fourchette}`;
  }
  return 'Montant voté (estimation compagnies.org indisponible)';
}

/**
 * Versements notifiés d'un poste conventionnel, en clair (décision 42) :
 * montant, exercice au titre duquel il est dû, date de versement attendue.
 */
function mentionNotifie(pr: Charge['projection'], poste: string, exercice: number): string | null {
  const v = pr.versementsNotifies.filter((x) => x.poste === poste);
  const premier = v[0];
  if (!premier) return null;
  const sommes = v
    .map((x) => `${Number.isInteger(x.montant) ? euros(x.montant) : eurosCentimes(x.montant)}${x.auTitreDe !== exercice ? ` au titre de ${x.auTitreDe}` : ''}`)
    .join(' et ');
  const dates = [...new Set(v.map((x) => x.libelleVersement))].join(', ');
  return `Notifié le ${dateFr(premier.notifieLe)} : ${sommes}, versement attendu ${dates}`;
}

/** Libellé d'une ligne d'exécution. Les écritures non classées renvoient vers l'écran « À arbitrer ». */
function libelleLigne(p: ExecutionPoste): (string | Node)[] {
  if (p.poste === A_CLASSER || p.poste === A_CLASSER_RECETTES) {
    return [p.libelle, h('span', { classe: 'note-lecture' }, ' — voir « À arbitrer »')];
  }
  return [p.libelle];
}

/**
 * Lignes d'un tableau de charges groupées par poste : un en-tête avec le
 * sous-total, puis les comptes en retrait. Les sorties non classées restent une
 * ligne seule qui renvoie vers « À arbitrer ».
 */
function lignesParPoste(lignes: readonly ExecutionPoste[], budgetes: ReadonlySet<string>): HTMLElement[] {
  return grouperLignesParPoste(lignes, budgetes).flatMap((g) => {
    const seule = g.lignes[0];
    if (estALClasser(g.poste) && seule) {
      return [
        h(
          'tr',
          {},
          h('th', { scope: 'row' }, ...libelleLigne(seule)),
          h('td', { classe: 'num' }, montantTracable(seule.poste, seule.libelle, seule.realise, 'depense')),
        ),
      ];
    }
    // Un seul compte porte tout le poste : une seule ligne, le compte en suffixe atténué.
    const unique = compteUnique(g);
    if (unique) {
      return [
        h(
          'tr',
          { classe: 'ligne-poste' },
          h(
            'th',
            { scope: 'row' },
            h('strong', {}, g.libelle),
            // Le générique porte le nom du poste : rien à ajouter.
            unique.poste === g.poste ? null : h('span', { classe: 'note-lecture' }, ` — ${unique.libelle}`),
          ),
          h('td', { classe: 'num' }, montantTracable(unique.poste, unique.libelle, unique.realise, 'depense')),
        ),
      ];
    }
    return [
      h(
        'tr',
        { classe: 'ligne-poste' },
        h('th', { scope: 'row' }, h('strong', {}, g.libelle)),
        h('td', { classe: 'num' }, h('strong', {}, euros(g.sousTotal))),
      ),
      ...g.lignes.map((p) =>
        h(
          'tr',
          { classe: 'ligne-compte' },
          h('th', { scope: 'row' }, p.libelle),
          h('td', { classe: 'num' }, montantTracable(p.poste, p.libelle, p.realise, 'depense')),
        ),
      ),
    ];
  });
}

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** `2026-09` → « septembre 2026 ». */
function moisFr(mois: string): string {
  return `${MOIS[Number(mois.slice(5, 7)) - 1] ?? mois} ${mois.slice(0, 4)}`;
}

function teintePart(part: number | null): string {
  if (part === null) return 'color-slate';
  if (part >= 0.7) return 'color-green';
  if (part >= 0.25) return 'color-amber';
  return 'color-danger';
}

/** Montant saisi en français : espaces et virgule décimale admis. */
function lireMontant(texte: string): number | null {
  const brut = texte.replace(/[^\d,.-]/g, '').replace(',', '.');
  if (brut === '' || brut === '-') return null;
  const n = Number(brut);
  return Number.isFinite(n) ? n : null;
}

export interface OptionsExercice {
  /** L'équipe (rôle administration) estime le reste à engager et ajoute des dépenses prévues. */
  readonly peutModifier?: boolean;
  /** Recharge le tableau de bord après une modification. */
  readonly recharger?: () => void;
}

export function vueExercice(d: Charge, o: OptionsExercice = {}): HTMLElement {
  const ex = d.execution;
  const pr = d.projection;
  const ch = d.chainage;
  const an = d.meta.exerciceCourant;
  const atterrissage = pr.methode === 'atterrissage';
  const paie = d.atterrissagePaie;
  const modifiable = atterrissage && o.peutModifier === true;

  // Retour des modifications du reste à engager, annoncé aux lecteurs d'écran.
  const message = h('div', { classe: 'barre-actions', role: 'status', 'aria-live': 'polite' });
  const signaler = (texte: string): void => {
    vider(message);
    message.appendChild(h('span', { classe: 'titre' }, texte));
  };
  const agir = (action: Promise<unknown>): void => {
    void action
      .then(() => {
        o.recharger?.();
      })
      .catch((e: unknown) => {
        signaler(e instanceof Error ? e.message : 'Modification refusée.');
      });
  };

  // ── Section 1 : exécution constatée ───────────────────────────────────
  const kpis = h(
    'div',
    { classe: 'kpi-grid' },
    kpi('warning', keuros(ex.encaissements).split(' ')[0] ?? '', 'k€', 'Encaissements depuis janvier', {
      sens: 'down',
      texte: `▼ ${pourcent(d.tauxExecution.produits, 0)} du budget de produits`,
    }),
    kpi(
      'neutral',
      keuros(ex.decaissements).split(' ')[0] ?? '',
      'k€',
      `Décaissements, dont ${keuros(ex.decaissementsRattaches)} rattachés à ${an}`,
      { sens: 'neutre', texte: `${pourcent(d.tauxExecution.charges, 0)} du budget de charges` },
    ),
    kpi(
      'alert',
      keuros(ex.soldeTresorerie).split(' ')[0] ?? '',
      'k€',
      "Solde de trésorerie de l'exercice",
      { sens: 'down', texte: `▼ au ${dateFr(d.meta.dateArrete)}` },
    ),
    kpi(
      'progress',
      keuros(pr.resteARecevoir).split(' ')[0] ?? '',
      'k€',
      'Ressources restant à recevoir d’ici décembre',
      { sens: 'up', texte: `▲ dont ${keuros(pr.resteARecevoirConventionnel)} conventionnelles` },
    ),
  );

  const produits = ex.produitsParPoste.filter((p) => p.budgete !== null || p.realise !== 0);
  const tableProduits = h(
    'table',
    { classe: 'bar-table encadre-tableau' },
    h('caption', { classe: 'invisible' }, `Encaissements ${an} par origine`),
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { scope: 'col' }, 'Ressource'),
        h('th', { scope: 'col' }, 'Part du montant budgété déjà encaissé'),
        h('th', { scope: 'col' }, 'Encaissé'),
      ),
    ),
    h(
      'tbody',
      {},
      ...produits.map((p) =>
        h(
          'tr',
          {},
          h('th', { scope: 'row' }, ...libelleLigne(p)),
          h(
            'td',
            {},
            barre(
              p.tauxRealisation ?? 1,
              p.tauxRealisation === null ? 'color-slate' : teintePart(p.tauxRealisation),
              p.tauxRealisation === null ? 'hors budget' : pourcent(p.tauxRealisation, 0),
            ),
          ),
          h('td', {}, p.nbEcritures > 0 ? montantTracable(p.poste, p.libelle, p.realise, 'recette') : euros(p.realise)),
        ),
      ),
    ),
  );

  const horsBudget = ex.produitsParPoste.filter((p) => p.budgete === null && p.realise > 0);
  const noteProduits = noteLecture(
    `Note de lecture : les pourcentages rapportent l'encaissé au montant inscrit au prévisionnel ${an}. ` +
      (horsBudget.length > 0
        ? `Les ressources hors budget — ${horsBudget.map((p) => `${p.libelle.toLowerCase()} ${eurosCentimes(p.realise)}`).join(', ')} — portent le total encaissé à ${eurosCentimes(ex.encaissements)}.`
        : `Le total encaissé s'établit à ${eurosCentimes(ex.encaissements)}.`),
  );

  const factCourante = d.facturation.lignes.find((f) => f.exercice === an);
  const factPrecedente = d.facturation.lignes.find((f) => f.exercice === an - 1);
  const alerteFacturation =
    factCourante && factPrecedente
      ? insight(
          d.facturation.ratio < 0.7 ? 'warning' : 'info',
          '⚠',
          `**Rythme d'appel des cotisations :** ${entier(factCourante.nbFactures)} factures émises pour ` +
            `${eurosCentimes(factCourante.montant)} au ${dateFr(d.meta.dateArrete)}, contre ` +
            `${entier(factPrecedente.nbFactures)} factures et ${eurosCentimes(factPrecedente.montant)} à la même date ` +
            `en ${an - 1}, soit ${pourcent(d.facturation.ratio, 0)} du rythme de l'exercice précédent. ` +
            `La projection de clôture retient l'intégralité des ${eurosCentimes(d.previsionnelVote.cotisations)} votés.`,
        )
      : null;

  // Une ressource à zéro dont le versement est notifié n'est plus à relancer :
  // elle est attendue, à une date connue (décision 42).
  const notifie = (p: ExecutionPoste): boolean => pr.versementsNotifies.some((v) => v.poste === p.poste);
  const aZeroTous = ex.produitsParPoste.filter((p) => p.budgete !== null && p.budgete > 0 && p.realise === 0);
  const aZero = aZeroTous.filter((p) => !notifie(p));
  const attendus = aZeroTous.filter(notifie);
  const alerteNotifies =
    attendus.length > 0
      ? insight(
          'info',
          '✦',
          `**Versements notifiés, attendus d'ici la clôture :** ` +
            attendus
              .map((p) => `${p.libelle} — ${(mentionNotifie(pr, p.poste, an) ?? '').replace(/^Notifié le [^:]+: /, '')}`)
              .join(' ; ') +
            `. La projection de clôture retient ces sommes à la place des montants votés, soit ` +
            // Les seuls postes notifiés : l'écart conventionnel total compte aussi
            // les ressources déjà encaissées au-delà de leur cible (décision 45).
            `${eurosSigne(attendus.reduce((s, p) => s + p.projete - (p.budgete ?? 0), 0))} sur les ressources conventionnelles.`,
        )
      : null;
  const alerteZero =
    aZero.length > 0
      ? insight(
          'negative',
          '⚠',
          `**${aZero.length} ressource${aZero.length > 1 ? 's' : ''} conventionnelle${aZero.length > 1 ? 's' : ''} encore à zéro :** ` +
            aZero.map((p) => `${p.libelle} (${euros(p.budgete ?? 0)})`).join(', ') +
            `. La projection de clôture les retient intégralement, pour ${euros(pr.resteARecevoirConventionnel)} restant à encaisser.`,
        )
      : null;

  // ── Recouvrement et cotisation moyenne ────────────────────────────────

  /**
   * Deux grandeurs distinctes, souvent confondues :
   *
   *   — le **taux de règlement** rapporte les cotisations acquittées aux
   *     cotisations appelées, à la date d'arrêté ;
   *   — la **cotisation moyenne constatée** ne porte que sur les adhérents qui
   *     ont déjà réglé. Elle se compare au produit moyen implicite de la
   *     projection, non à la moyenne d'un exercice clos.
   *
   * La moyenne constatée en cours d'exercice est biaisée : les grosses
   * structures règlent souvent les premières. La mention accompagne le chiffre.
   */
  const adh = d.adherents;
  const payeurs = adh.serieCourante?.payeurs ?? 0;
  const acquittees = adh.serieCourante?.cotisationsAcquittees ?? 0;
  const tauxReglement = adh.appelsExerciceCourant > 0 ? payeurs / adh.appelsExerciceCourant : 0;
  const moyenneConstatee = payeurs > 0 ? acquittees / payeurs : 0;
  const ecartMoyenne = moyenneConstatee - adh.produitMoyenImplicite;
  const ecartRelatif =
    adh.produitMoyenImplicite > 0 ? ecartMoyenne / adh.produitMoyenImplicite : 0;

  const suiviCotisations = h(
    'div',
    { classe: 'kpi-grid' },
    kpi(
      tauxReglement >= 0.7 ? 'progress' : tauxReglement >= 0.4 ? 'warning' : 'alert',
      pourcent(tauxReglement, 0),
      null,
      'Taux de règlement des cotisations appelées',
      {
        sens: tauxReglement >= 0.7 ? 'up' : 'down',
        texte: `${entier(payeurs)} réglées sur ${entier(adh.appelsExerciceCourant)} appelées`,
      },
    ),
    kpi(
      'neutral',
      entier(adh.appelsExerciceCourant - payeurs),
      null,
      'Cotisations appelées restant à encaisser',
      {
        sens: 'neutre',
        texte:
          d.projection.sourceCotisations === 'estimation'
            ? `soit ${euros(d.projection.resteARecevoirCotisations)} d’après l’estimation de fin de campagne`
            : `soit ${euros(d.projection.resteARecevoirCotisations)} au montant voté`,
      },
    ),
    kpi(
      ecartMoyenne >= 0 ? 'progress' : 'warning',
      eurosCentimes(moyenneConstatee).replace(' €', ''),
      '€',
      'Cotisation moyenne constatée',
      {
        sens: ecartMoyenne >= 0 ? 'up' : 'down',
        texte: `${ecartMoyenne >= 0 ? '▲' : '▼'} ${eurosSigne(ecartMoyenne)} vs projection`,
      },
    ),
    kpi(
      'neutral',
      eurosCentimes(adh.produitMoyenImplicite).replace(' €', ''),
      '€',
      'Produit moyen retenu à la projection',
      {
        sens: 'neutre',
        texte: `${entier(adh.payeursProjetes)} payeurs attendus en fin d’exercice`,
      },
    ),
  );

  const insightCotisations = insight(
    ecartMoyenne >= 0 ? 'positive' : 'warning',
    ecartMoyenne >= 0 ? '✦' : '⚠',
    ecartMoyenne >= 0
      ? `**La cotisation moyenne constatée dépasse la projection de ${pourcent(ecartRelatif, 1)}.** ` +
          `${eurosCentimes(moyenneConstatee)} par adhérent réglé contre ` +
          `${eurosCentimes(adh.produitMoyenImplicite)} retenus. À taux de recouvrement inchangé, la cible de ` +
          `${eurosCentimes(d.previsionnelVote.cotisations)} serait dépassée de ` +
          `${eurosCentimes(Math.max(0, moyenneConstatee * adh.payeursProjetes - d.previsionnelVote.cotisations))}. ` +
          `La réserve tient toutefois : les premiers règlements viennent souvent des structures les plus ` +
          `importantes, et la moyenne se tasse en fin d’exercice.`
      : `**La cotisation moyenne constatée est inférieure de ${pourcent(Math.abs(ecartRelatif), 1)} à la projection.** ` +
          `${eurosCentimes(moyenneConstatee)} par adhérent réglé contre ` +
          `${eurosCentimes(adh.produitMoyenImplicite)} retenus. À effectif projeté inchangé, la cible de ` +
          `${eurosCentimes(d.previsionnelVote.cotisations)} serait manquée de ` +
          `${eurosCentimes(Math.abs(moyenneConstatee * adh.payeursProjetes - d.previsionnelVote.cotisations))}.`,
  );

  // ── Charges ───────────────────────────────────────────────────────────
  // Un poste de personnel voté reste visible à zéro ; les autres postes ne le sont que mouvementés.
  const postesPersonnelBudgetes = new Set(
    d.ecartsPostes.filter((x) => x.categorie === 'Charges de personnel' && x.budgete > 0).map((x) => x.code),
  );
  const cartePersonnel = h(
    'div',
    { classe: 'card' },
    h('h3', {}, 'Charges de personnel'),
    h(
      'table',
      { classe: 'bar-table' },
      h('caption', { classe: 'invisible' }, 'Charges de personnel rattachées à l’exercice'),
      h(
        'tbody',
        {},
        ...lignesParPoste(ex.chargesPersonnel, postesPersonnelBudgetes),
        h(
          'tr',
          {},
          h('th', { scope: 'row' }, h('strong', {}, 'Total')),
          h('td', { classe: 'num' }, h('strong', {}, eurosCentimes(ex.totalChargesPersonnel))),
        ),
      ),
    ),
    noteLecture(
      `Soit ${pourcent(ex.totalChargesPersonnel / d.previsionnelVote.charges.personnel)} de l'enveloppe de ` +
        `personnel budgétée (${eurosCentimes(d.previsionnelVote.charges.personnel)}). Le prévisionnel est établi en brut, ` +
        `les décaissements en net augmenté du prélèvement à la source, la part salariale étant incluse dans les ` +
        `règlements d'organismes sociaux.`,
    ),
  );

  const carteAutres = h(
    'div',
    { classe: 'card' },
    h('h3', {}, 'Principaux autres décaissements'),
    h(
      'table',
      { classe: 'bar-table' },
      h('caption', { classe: 'invisible' }, 'Autres décaissements rattachés à l’exercice'),
      h(
        'tbody',
        {},
        ...lignesParPoste(ex.autresCharges, new Set()),
        h(
          'tr',
          {},
          h('th', { scope: 'row' }, h('strong', {}, 'Total')),
          h('td', { classe: 'num' }, h('strong', {}, eurosCentimes(ex.totalAutresCharges))),
        ),
      ),
    ),
    noteLecture(
      `Décaissements rattachés à l'exercice ${an}. ${eurosCentimes(ex.decaissementsAutresExercices)} de sorties ` +
        `de la période sont rattachées à un autre exercice et n'entrent pas dans ce total.`,
    ),
  );

  // ── Section 2 : projection ────────────────────────────────────────────

  /**
   * Décomposition de la projection de charges.
   *
   * Deux sous-agrégats sont budgétés — le personnel et les autres charges —
   * et leur projection vient du budget construit : masse salariale calculée
   * salarié par salarié pour le premier, enveloppe votée répartie au prorata
   * de l'exécution pour le second.
   *
   * Le reliquat porte les dépassements sur comptes hors nomenclature et
   * l'écart d'arbitrage de la projection votée en séance. Il est affiché plutôt
   * que dissous : la somme des trois lignes redonne exactement la projection.
   */
  const chargesBudget = d.ecartsPostes.filter((x) => x.nature === 'charge');
  const personnelProjete = chargesBudget
    .filter((x) => x.categorie === 'Charges de personnel')
    .reduce((s, x) => s + x.projete, 0);
  const autresProjetees = chargesBudget
    .filter((x) => x.categorie !== 'Charges de personnel')
    .reduce((s, x) => s + x.projete, 0);
  const residuArbitrage = pr.chargesProjetees - personnelProjete - autresProjetees;
  const depassementConstate = pr.depassementHorsNomenclature;
  // Avec l'atterrissage, le réalisé du personnel est le versé des bulletins,
  // non le décaissé en banque (décision 45).
  const personnelRealise = atterrissage
    ? chargesBudget.filter((x) => x.categorie === 'Charges de personnel').reduce((s, x) => s + x.realise, 0)
    : ex.totalChargesPersonnel;
  const libelleReliquat = atterrissage
    ? 'dont réalisé hors nomenclature'
    : 'dont dépassements hors nomenclature et écart d’arbitrage';

  const ligne = (
    libelle: string,
    vote: number,
    realise: number,
    projete: number,
    ecart: number,
    gras = false,
  ): HTMLElement =>
    h(
      'tr',
      {},
      h('th', { scope: 'row' }, gras ? h('strong', {}, libelle) : libelle),
      h('td', { classe: 'num' }, euros(vote)),
      h('td', { classe: 'num' }, euros(realise)),
      h('td', { classe: 'num' }, euros(projete)),
      h(
        'td',
        {},
        ecart === 0
          ? badge('favorable', '= équilibre')
          : badge(ecart > 0 ? 'favorable' : 'vigilance', `${ecart > 0 ? '▲' : '▼'} ${eurosSigne(ecart)}`),
      ),
    );

  const tableProjection = h(
    'table',
    { classe: 'comp-table' },
    h('caption', { classe: 'invisible' }, `Projection de clôture ${an}`),
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { scope: 'col' }, 'Agrégat'),
        h('th', { scope: 'col' }, `Prévisionnel ${an}`),
        h('th', { scope: 'col' }, `Réalisé au ${dateFr(d.meta.dateArrete)}`),
        h('th', { scope: 'col' }, 'Projection au 31/12'),
        h('th', { scope: 'col' }, 'Écart au prévisionnel'),
      ),
    ),
    h(
      'tbody',
      {},
      ligne('Produits', pr.produitsVotes, ex.encaissements, pr.produitsProjetes, pr.produitsProjetes - pr.produitsVotes, true),
      ...['cotisations', 'fcap_ccneac', 'fcap_ccnsvp', 'dgca_professionnalisation', 'dgca_theatre', 'audiens']
        .map((cle) => ex.produitsParPoste.find((p) => p.poste === cle))
        .filter((p): p is NonNullable<typeof p> => p !== undefined)
        .map((p) =>
          h(
            'tr',
            {},
            h(
              'th',
              { scope: 'row' },
              `dont ${p.libelle.toLowerCase()}`,
              ...(p.poste === 'cotisations' ? [h('div', { classe: 'note-lecture' }, mentionCotisations(pr))] : []),
              ...((m) => (m ? [h('div', { classe: 'note-lecture' }, m)] : []))(mentionNotifie(pr, p.poste, an)),
            ),
            h('td', { classe: 'num' }, euros(p.budgete ?? 0)),
            h('td', { classe: 'num' }, euros(p.realise)),
            h('td', { classe: 'num' }, euros(p.projete)),
            h(
              'td',
              {},
              p.realise === 0 && mentionNotifie(pr, p.poste, an) !== null
                ? badge(
                    p.projete >= (p.budgete ?? 0) ? 'favorable' : 'vigilance',
                    `${eurosSigne(p.projete - (p.budgete ?? 0))} · notifié`,
                  )
                : badge(p.realise > 0 ? 'favorable' : 'alerte', p.realise > 0 ? '= en cours' : '= à relancer'),
            ),
          ),
        ),
      ligne(
        'Charges',
        pr.chargesVotees,
        atterrissage
          ? chargesBudget.reduce((s, x) => s + x.realise, 0) + depassementConstate
          : ex.decaissementsRattaches,
        pr.chargesProjetees,
        pr.chargesVotees - pr.chargesProjetees,
        true,
      ),
      h(
        'tr',
        {},
        h(
          'th',
          { scope: 'row' },
          'dont charges de personnel',
          paie?.dernierMois
            ? h('div', { classe: 'note-lecture' }, `Réalisé : versé sur les bulletins jusqu'à ${moisFr(paie.dernierMois)}`)
            : null,
        ),
        h('td', { classe: 'num' }, euros(d.previsionnelVote.charges.personnel)),
        h('td', { classe: 'num' }, euros(personnelRealise)),
        h('td', { classe: 'num' }, euros(personnelProjete)),
        h(
          'td',
          {},
          badge(
            personnelProjete <= d.previsionnelVote.charges.personnel ? 'favorable' : 'vigilance',
            eurosSigne(d.previsionnelVote.charges.personnel - personnelProjete),
          ),
        ),
      ),
      h(
        'tr',
        {},
        h('th', { scope: 'row' }, 'dont autres charges'),
        h('td', { classe: 'num' }, euros(d.previsionnelVote.charges.autres)),
        h('td', { classe: 'num' }, euros(ex.totalAutresCharges)),
        h('td', { classe: 'num' }, euros(autresProjetees)),
        h(
          'td',
          {},
          badge(
            autresProjetees <= d.previsionnelVote.charges.autres ? 'favorable' : 'vigilance',
            eurosSigne(d.previsionnelVote.charges.autres - autresProjetees),
          ),
        ),
      ),
      h(
        'tr',
        {},
        h('th', { scope: 'row' }, libelleReliquat),
        h('td', { classe: 'num' }, '—'),
        h('td', { classe: 'num' }, euros(depassementConstate)),
        h('td', { classe: 'num' }, euros(residuArbitrage)),
        h('td', {}, badge('vigilance', `▲ ${eurosSigne(-residuArbitrage)}`)),
      ),
      ligne('Résultat', pr.resultatVote, ex.soldeTresorerie, pr.resultatProjete, pr.ecartResultat, true),
    ),
  );

  // ── Section 3 : chaînage des fonds propres ────────────────────────────
  const tableChainage = h(
    'table',
    { classe: 'comp-table' },
    h('caption', { classe: 'invisible' }, 'Chaînage des fonds propres'),
    h(
      'thead',
      {},
      h('tr', {}, h('th', { scope: 'col' }, 'Étape'), h('th', { scope: 'col' }, 'Montant'), h('th', { scope: 'col' }, 'Commentaire')),
    ),
    h(
      'tbody',
      {},
      h(
        'tr',
        {},
        h('th', { scope: 'row' }, `Fonds propres avant affectation du résultat ${d.cloture.exercice}`),
        h('td', { classe: 'num' }, eurosCentimes(ch.fondsPropresAvantAffectation)),
        h('td', {}, 'Report des exercices antérieurs'),
      ),
      h(
        'tr',
        {},
        h('th', { scope: 'row' }, h('strong', {}, `Résultat de l'exercice ${d.cloture.exercice}`)),
        h('td', { classe: 'num' }, badge(ch.resultatExercicePrecedent >= 0 ? 'favorable' : 'alerte', eurosSigne(ch.resultatExercicePrecedent))),
        h('td', {}, ch.resultatExercicePrecedent >= 0 ? 'Exercice bénéficiaire après retraitements' : 'Exercice déficitaire'),
      ),
      h(
        'tr',
        {},
        h('th', { scope: 'row' }, h('strong', {}, `Fonds propres au 31 décembre ${d.cloture.exercice}`)),
        h('td', { classe: 'num' }, h('strong', {}, eurosCentimes(ch.fondsPropresCloture))),
        h('td', {}, `▲ ${eurosSigne(ch.ecartAuPrevisionnel)} par rapport au prévisionnel`),
      ),
      h(
        'tr',
        {},
        h('th', { scope: 'row' }, `Résultat ${an} projeté`),
        h('td', { classe: 'num' }, badge(ch.resultatProjete >= 0 ? 'favorable' : 'alerte', eurosSigne(ch.resultatProjete))),
        h('td', {}, 'Projection de clôture ci-dessus'),
      ),
      h(
        'tr',
        {},
        h('th', { scope: 'row' }, h('strong', {}, `Réserve attendue au 31 décembre ${an}`)),
        h('td', { classe: 'num' }, h('strong', {}, eurosCentimes(ch.reserveAttendue))),
        h('td', {}, `Soit ${ch.multipleSeuil.toFixed(1).replace('.', ',')} fois le seuil de sécurité ${an}`),
      ),
    ),
  );

  // ── Détail des charges, poste par poste ───────────────────────────────

  const champMontant = (valeur: number, libelle: string, surValide: (n: number | null) => void): HTMLElement =>
    h('input', {
      classe: 'champ-montant',
      type: 'text',
      inputmode: 'decimal',
      value: nombre(valeur, 0),
      'aria-label': libelle,
      surChange: (ev) => {
        surValide(lireMontant((ev.target as HTMLInputElement).value));
      },
    });

  /** Reste à engager d'un poste : estimable par l'équipe, sauf la paie lue sur les bulletins. */
  const celluleReste = (x: (typeof chargesBudget)[number]): HTMLElement => {
    const a = x.atterrissage;
    if (!a) return h('td', {}, euros(x.resteAReconnaitre));
    if (a.source === 'bulletins') {
      return h('td', {}, euros(x.resteAReconnaitre), h('div', { classe: 'note-lecture' }, 'à verser, bulletins'));
    }
    // Les dépenses prévues ont leurs propres lignes : la cellule porte le reste du poste seul.
    const resteSeul = a.saisie ? a.saisie.montant : a.resteCalcule;
    return h(
      'td',
      { classe: a.source === 'budget' ? 'engendre' : '' },
      modifiable
        ? champMontant(resteSeul, `Reste à engager, ${x.libelle}`, (n) => {
            if (n === null || n < 0) {
              signaler('Indiquez un montant positif ou nul.');
              return;
            }
            agir(estimerResteAEngager(x.code, n, a.saisie?.commentaire ?? null));
          })
        : euros(resteSeul),
      a.saisie
        ? h(
            'div',
            { classe: 'note-lecture' },
            `estimé par ${a.saisie.modifiePar.split('@')[0] ?? ''} (calcul : ${euros(a.resteCalcule)})`,
            modifiable
              ? h(
                  'button',
                  {
                    classe: 'btn-reset',
                    type: 'button',
                    surClick: () => {
                      agir(estimerResteAEngager(x.code, null));
                    },
                  },
                  'Revenir au calcul',
                )
              : null,
          )
        : null,
    );
  };

  /** Ligne d'une dépense prévue, sous son poste. */
  const ligneDepense = (dp: DepensePrevue): HTMLElement => {
    const enregistrer = (montant: number): void => {
      agir(modifierDepensePrevue(dp.id, { poste: dp.poste, libelle: dp.libelle, montant, commentaire: dp.commentaire }));
    };
    return h(
      'tr',
      { classe: 'ligne-compte' },
      h(
        'th',
        { scope: 'row' },
        `Dépense prévue : ${dp.libelle}`,
        dp.commentaire ? h('span', { classe: 'note-lecture' }, ` — ${dp.commentaire}`) : null,
      ),
      h('td', {}, ''),
      h('td', {}, ''),
      h('td', {}, ''),
      h(
        'td',
        {},
        modifiable
          ? champMontant(dp.montant, `Montant de la dépense prévue ${dp.libelle}`, (n) => {
              if (n === null || n < 0) {
                signaler('Indiquez un montant positif ou nul.');
                return;
              }
              enregistrer(n);
            })
          : euros(dp.montant),
      ),
      h('td', {}, ''),
      h(
        'td',
        {},
        modifiable
          ? h(
              'button',
              {
                classe: 'btn-reset',
                type: 'button',
                surClick: () => {
                  agir(supprimerDepensePrevue(dp.id));
                },
              },
              'Supprimer',
            )
          : '',
      ),
    );
  };

  const ligneCharge = (x: (typeof chargesBudget)[number]): HTMLElement[] => [
    h(
      'tr',
      {},
      h('th', { scope: 'row' }, x.libelle),
      h('td', {}, euros(x.budgete)),
      h('td', {}, x.nbEcritures > 0 ? montantTracable(x.code, x.libelle, x.realise, 'depense') : euros(x.realise)),
      h('td', {}, x.tauxRealisation === null ? '—' : pourcent(x.tauxRealisation, 0)),
      celluleReste(x),
      h('td', {}, euros(x.projete)),
      h(
        'td',
        {},
        x.ecart === 0
          ? badge('favorable', '=')
          : badge(x.ecart < 0 ? 'favorable' : 'alerte', eurosSigne(-x.ecart)),
      ),
    ),
    ...(x.atterrissage?.depenses ?? []).map(ligneDepense),
  ];

  const groupeCharges = (categorie: string, lignes: typeof chargesBudget): HTMLElement[] => {
    if (lignes.length === 0) return [];
    const t = (f: (x: (typeof chargesBudget)[number]) => number): number =>
      lignes.reduce((s, x) => s + f(x), 0);
    return [
      h(
        'tr',
        { classe: 'categorie' },
        h('th', { scope: 'row' }, categorie),
        h('td', {}, euros(t((x) => x.budgete))),
        h('td', {}, euros(t((x) => x.realise))),
        h(
          'td',
          {},
          t((x) => x.budgete) > 0 ? pourcent(t((x) => x.realise) / t((x) => x.budgete), 0) : '—',
        ),
        h('td', {}, euros(t((x) => x.resteAReconnaitre))),
        h('td', {}, euros(t((x) => x.projete))),
        h('td', {}, ''),
      ),
      ...lignes.flatMap(ligneCharge),
    ];
  };

  const categoriesCharges = [...new Set(chargesBudget.map((x) => x.categorie))];

  const tableDetailCharges = h(
    'div',
    { classe: 'enveloppe-table' },
    h(
      'table',
      { classe: 'table-budget' },
      h('caption', { classe: 'invisible' }, `Détail des charges projetées ${an}`),
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { scope: 'col' }, 'Poste de charge'),
          h('th', { scope: 'col' }, 'Budget de référence'),
          h('th', { scope: 'col' }, `Réalisé au ${dateFr(d.meta.dateArrete)}`),
          h('th', { scope: 'col' }, 'Taux'),
          h('th', { scope: 'col' }, 'Reste à engager'),
          h('th', { scope: 'col' }, 'Projeté au 31/12'),
          h('th', { scope: 'col' }, 'Écart'),
        ),
      ),
      h(
        'tbody',
        {},
        ...categoriesCharges.flatMap((c) =>
          groupeCharges(
            c,
            chargesBudget.filter((x) => x.categorie === c),
          ),
        ),
        h(
          'tr',
          { classe: 'total' },
          h('th', { scope: 'row' }, 'Total des charges budgétées'),
          h('td', {}, euros(chargesBudget.reduce((s, x) => s + x.budgete, 0))),
          h('td', {}, euros(atterrissage ? chargesBudget.reduce((s, x) => s + x.realise, 0) : ex.decaissementsRattaches)),
          h('td', {}, ''),
          h('td', {}, ''),
          h('td', {}, euros(personnelProjete + autresProjetees)),
          h('td', {}, ''),
        ),
        h(
          'tr',
          {},
          h(
            'th',
            { scope: 'row' },
            atterrissage ? 'Réalisé hors nomenclature' : 'Dépassements hors nomenclature et écart d’arbitrage',
          ),
          h('td', {}, '—'),
          h('td', {}, euros(depassementConstate)),
          h('td', {}, ''),
          h('td', {}, ''),
          h('td', {}, euros(residuArbitrage)),
          h('td', {}, ''),
        ),
        h(
          'tr',
          { classe: 'resultat' },
          h('th', { scope: 'row' }, 'Projection de charges au 31 décembre'),
          h('td', {}, euros(pr.chargesVotees)),
          h(
            'td',
            {},
            euros(
              atterrissage
                ? chargesBudget.reduce((s, x) => s + x.realise, 0) + depassementConstate
                : ex.decaissementsRattaches,
            ),
          ),
          h('td', {}, pourcent(d.tauxExecution.charges, 0)),
          h('td', {}, ''),
          h('td', {}, euros(pr.chargesProjetees)),
          h('td', {}, badge('vigilance', eurosSigne(pr.chargesVotees - pr.chargesProjetees))),
        ),
      ),
    ),
  );

  // ── Paie d'après les bulletins (décision 45) ──────────────────────────
  const tablePaie = paie
    ? h(
        'div',
        { classe: 'enveloppe-table' },
        h(
          'table',
          { classe: 'table-budget table-paie' },
          h('caption', { classe: 'invisible' }, `Charges de personnel ${an} par salarié, d'après les bulletins`),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { scope: 'col' }, 'Salarié'),
              h('th', { scope: 'col' }, 'Brut versé'),
              h('th', { scope: 'col' }, 'Cotisations versées'),
              h('th', { scope: 'col' }, 'Brut à verser'),
              h('th', { scope: 'col' }, 'Cotisations à verser'),
              h('th', { scope: 'col' }, 'Titres-restaurant'),
              h('th', { scope: 'col' }, 'Transport'),
              h('th', { scope: 'col' }, 'Prime de partage'),
              h('th', { scope: 'col' }, 'Coût projeté'),
            ),
          ),
          h(
            'tbody',
            {},
            ...[...paie.salaries, null].map((s) => {
              const v = s ? s.verse : paie.verse;
              const r = s ? s.resteAVerser : paie.resteAVerser;
              const p = s ? s.projete : paie.projete;
              return h(
                'tr',
                { classe: s ? '' : 'total' },
                h('th', { scope: 'row' }, s ? s.nom : 'Total'),
                h('td', {}, euros(v.salaires_bruts)),
                h('td', {}, euros(v.charges_patronales)),
                h('td', {}, euros(r.salaires_bruts)),
                h('td', {}, euros(r.charges_patronales)),
                h('td', {}, euros(p.titres_restaurant)),
                h('td', {}, euros(p.transport_salaries)),
                h('td', {}, euros(p.primes_partage_valeur)),
                h('td', {}, h('strong', {}, euros(s ? s.coutProjete : paie.coutProjete))),
              );
            }),
          ),
        ),
      )
    : null;

  const notesPaie = paie
    ? h(
        'ul',
        { classe: 'text-block' },
        ...paie.salaries.flatMap((s) => [
          ...(s.rappel
            ? [
                h(
                  'li',
                  {},
                  `${s.nom} : salaire décidé appliqué sur les ${s.moisRestants} mois restants, et rappel de ` +
                    `${eurosCentimes(s.rappel.brut)} brut (${eurosCentimes(s.rappel.cotisations)} de cotisations) ` +
                    `sur les ${s.rappel.mois} mois déjà versés.`,
                ),
              ]
            : []),
          ...s.elements.map((x) =>
            h('li', {}, `${s.nom} — ${x.libelle} : ${eurosCentimes(x.montant)}, prévu en ${moisFr(x.moisPrevu)}. ${x.source}`),
          ),
        ]),
      )
    : null;

  // ── Dépenses prévues : ajout ──────────────────────────────────────────
  const postesEstimables = chargesBudget.filter((x) => x.atterrissage && x.atterrissage.source !== 'bulletins');
  const formulaireDepense = modifiable
    ? (() => {
        const poste = h(
          'select',
          { classe: 'ctrl-select', id: 'depense-prevue-poste' },
          ...postesEstimables.map((x) => h('option', { value: x.code, selected: x.code === 'autres_postes' }, x.libelle)),
        );
        const libelle = h('input', { classe: 'ctrl-select', type: 'text', id: 'depense-prevue-libelle', maxlength: '120' });
        const montant = h('input', { classe: 'champ-montant', type: 'text', inputmode: 'decimal', id: 'depense-prevue-montant' });
        const commentaire = h('input', { classe: 'ctrl-select', type: 'text', id: 'depense-prevue-commentaire', maxlength: '500' });
        return h(
          'div',
          { classe: 'text-block' },
          h('p', {}, h('strong', {}, 'Ajouter une dépense prévue'), ` — une dépense nouvelle d'ici le 31 décembre ${an}, par exemple du mobilier`),
          h(
            'form',
            {
              classe: 'editeur-champs',
              surSubmit: (ev) => {
                ev.preventDefault();
                const m = lireMontant(montant.value);
                if (libelle.value.trim() === '' || m === null || m < 0) {
                  signaler('Indiquez un libellé et un montant.');
                  return;
                }
                agir(
                  ajouterDepensePrevue({
                    poste: poste.value,
                    libelle: libelle.value.trim(),
                    montant: m,
                    commentaire: commentaire.value.trim() === '' ? null : commentaire.value.trim(),
                  }),
                );
              },
            },
            h('label', {}, 'Poste de rattachement', poste),
            h('label', {}, 'Libellé', libelle),
            h('label', {}, 'Montant estimé', montant),
            h('label', {}, 'Commentaire', commentaire),
            h('button', { classe: 'btn-action', type: 'submit' }, 'Ajouter'),
          ),
        );
      })()
    : null;

  return h(
    'div',
    { classe: 'page', id: 'page-exercice', role: 'region', 'aria-label': 'Exécution budgétaire' },
    h(
      'section',
      {},
      enteteSection(1, `Où en est réellement le SCC au ${dateFr(d.meta.dateArrete)}`, `Flux constatés en banque depuis le 1er janvier ${an}`),
      kpis,
      h(
        'p',
        { classe: 'text-block' },
        `La comptabilité ${an} est tenue en encaissements et décaissements : les montants ci-dessus sont des flux de ` +
          `trésorerie, non des charges et produits rattachés. Chaque total est cliquable et ouvre le détail des ` +
          `écritures qui le composent.`,
      ),
      regleAccent('Encaissements par origine'),
      graphiqueAnneau(d.repartitions.produitsRealises.parts, {
        titre: `Répartition des encaissements ${an}`,
        total: d.repartitions.produitsRealises.total,
        sousTitre: 'encaissés à ce jour',
        enteteAbscisse: 'Origine',
      }),
      noteLecture(
        `L'anneau porte le **réalisé**, non le voté : trois ressources conventionnelles encore à zéro y sont ` +
          `absentes, et c'est le message. Le prévisionnel voté répartit ` +
          `${eurosCentimes(d.repartitions.produitsVotes.total)} entre ` +
          `${d.repartitions.produitsVotes.parts.length} postes.`,
      ),
      tableProduits,
      noteProduits,
      barreExport('execution-produits', `Encaissements ${an} par origine`),
      alerteFacturation,
      alerteZero,
      alerteNotifies,
      regleAccent('Où en est le recouvrement des cotisations'),
      suiviCotisations,
      noteLecture(
        `Note de lecture : le taux de règlement rapporte les cotisations acquittées aux cotisations appelées. ` +
          `La cotisation moyenne constatée ne porte que sur les adhérents ayant déjà réglé ; elle se compare au ` +
          `produit moyen implicite de la projection, ${eurosCentimes(d.adherents.produitMoyenImplicite)}, qui ` +
          `résulte des ${eurosCentimes(d.previsionnelVote.cotisations)} votés rapportés aux ` +
          `${entier(d.adherents.payeursProjetes)} payeurs attendus.`,
      ),
      insightCotisations,
      regleAccent(`Décaissements de l'exercice ${an}`),
      graphiqueAnneau(d.repartitions.chargesRealisees.parts, {
        titre: `Répartition des décaissements ${an}`,
        total: d.repartitions.chargesRealisees.total,
        sousTitre: 'décaissés à ce jour',
        enteteAbscisse: 'Poste',
      }),
      h('div', { classe: 'two-col' }, cartePersonnel, carteAutres),
      barreExport('execution-charges', `Décaissements ${an}`),
    ),
    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(2, `Ce que devrait donner l'exercice ${an} à son terme`, 'Projection de clôture confrontée au prévisionnel voté'),
      atterrissage
        ? h(
            'p',
            { classe: 'text-block' },
            `La projection est un atterrissage : pour chaque poste de charge, le réalisé plus ce qu'il reste à ` +
              `engager d'ici décembre, soit ${eurosCentimes(pr.chargesProjetees)} de charges` +
              (paie?.dernierMois
                ? `, dont la paie lue sur les bulletins, salarié par salarié, jusqu'à ${moisFr(paie.dernierMois)}`
                : ``) +
              `. Les produits restent à leur cible budgétaire, sans jamais descendre sous l'encaissé` +
              (pr.sourceCotisations === 'estimation' ? `, les cotisations à l'estimation de fin de campagne` : ``) +
              (pr.versementsNotifies.length > 0 ? `, les ressources notifiées à la somme notifiée` : ``) +
              `. Atteindre cette projection de produits suppose d'encaisser ${eurosCentimes(pr.resteARecevoir)} ` +
              `d'ici décembre.`,
          )
        : null,
      h(
        'p',
        { classe: 'text-block', hidden: atterrissage },
        `La projection maintient les produits à leur cible budgétaire` +
          (pr.sourceCotisations === 'estimation' && pr.versementsNotifies.length > 0
            ? `, sauf les cotisations, projetées à l'estimation de fin de campagne, et les ressources dont le ` +
              `versement est notifié, projetées à la somme notifiée,`
            : pr.sourceCotisations === 'estimation'
              ? `, sauf les cotisations, projetées à l'estimation de fin de campagne,`
              : pr.versementsNotifies.length > 0
                ? `, sauf les ressources dont le versement est notifié, projetées à la somme notifiée,`
                : ``) +
          ` et porte les charges à ` +
          `${eurosCentimes(pr.chargesProjetees)}, soit les ${eurosCentimes(pr.chargesVotees)} votés augmentés des ` +
          `dépassements constatés sur des postes hors nomenclature budgétaire. Atteindre ` +
          `${pr.sourceCotisations === 'estimation' ? 'cette projection' : 'la cible'} de produits suppose ` +
          `d'encaisser ${eurosCentimes(pr.resteARecevoir)} d'ici décembre.`,
      ),
      // La table de projection défile dans son cadre sur téléphone, pas la page.
      h('div', { classe: 'enveloppe-table' }, tableProjection),
      atterrissage
        ? noteLecture(
            `Note de lecture : les produits réalisés sont des encaissements ; les charges réalisées, le versé des ` +
              `bulletins pour la paie et le décaissé en banque pour le reste. L'écart de ` +
              `${eurosSigne(pr.ecartResultat)} au résultat se décompose en ` +
              `${eurosSigne(pr.chargesVotees - pr.chargesProjetees)} sur les charges et ` +
              `${eurosSigne(pr.produitsProjetes - pr.produitsVotes)} sur les produits` +
              (pr.sourceCotisations === 'estimation'
                ? `, dont ${eurosSigne(pr.ecartCotisations)} sur les cotisations`
                : ``) +
              (pr.ecartConventionnel !== 0
                ? `${pr.sourceCotisations === 'estimation' ? ' et' : ', dont'} ${eurosSigne(pr.ecartConventionnel)} sur les ressources conventionnelles`
                : ``) +
              `. Les trois lignes de charges se somment exactement à la projection.`,
          )
        : null,
      atterrissage ? null : noteLecture(
        `Note de lecture : la colonne « Réalisé » exprime des flux de trésorerie ; la colonne « Projection » les ` +
          `rapproche de la logique du prévisionnel. L'écart de ${eurosSigne(pr.ecartResultat)} au résultat provient ` +
          `du dépassement des charges` +
          (pr.sourceCotisations === 'estimation'
            ? `${pr.ecartConventionnel !== 0 ? ',' : ' et'} de l'écart des cotisations à leur montant voté (${eurosSigne(pr.ecartCotisations)})`
            : ``) +
          (pr.ecartConventionnel !== 0
            ? ` et de l'écart des ressources notifiées à leurs montants votés (${eurosSigne(pr.ecartConventionnel)})`
            : ``) +
          `. Les trois lignes de charges se somment exactement à la projection.`,
      ),
      barreExport('projection', `Projection de clôture ${an}`),

      ...(tablePaie && paie
        ? [
            regleAccent('Les charges de personnel, salarié par salarié'),
            h(
              'p',
              { classe: 'text-block' },
              `Versé : les bulletins de paie de janvier à ${moisFr(paie.dernierMois ?? '')}. À verser : les mois ` +
                `restants au salaire de base décidé, avec les cotisations au taux du dernier bulletin, les ` +
                `titres-restaurant à leur moyenne mensuelle et le Navigo ; s'y ajoutent les primes de partage de la ` +
                `valeur et le forfait mobilités, sans cotisations patronales. Coût employeur projeté : ` +
                `${eurosCentimes(paie.coutProjete)}, dont ${eurosCentimes(paie.coutVerse)} déjà versés.`,
            ),
            tablePaie,
            ...(notesPaie ? [notesPaie] : []),
            noteLecture(
              `Note de lecture : les cotisations patronales s'entendent hors titres-restaurant, qui ont leur colonne ; ` +
                `le transport réunit le Navigo (50 % à la charge de l'employeur) et le forfait mobilités. Chaque ` +
                `nouveau bulletin remplace un mois projeté par un mois versé.`,
            ),
            barreExport('atterrissage-paie', `Charges de personnel ${an} par salarié`),
          ]
        : []),

      regleAccent('Le détail des charges, poste par poste'),
      h(
        'p',
        { classe: 'text-block' },
        `Le prévisionnel voté ne détaille pas les charges : il porte une enveloppe de personnel de ` +
          `${eurosCentimes(d.previsionnelVote.charges.personnel)} et une enveloppe d'autres charges de ` +
          `${eurosCentimes(d.previsionnelVote.charges.autres)}. Le budget de référence ci-dessous en donne la maille : ` +
          `les charges de personnel sont calculées salarié par salarié depuis la grille de salaires, les autres ` +
          `charges répartissent l'enveloppe votée au prorata de l'exécution constatée, qui fournit la seule ` +
          `ventilation par compte qui soit juste.`,
      ),
      message,
      tableDetailCharges,
      noteLecture(
        atterrissage
          ? `Note de lecture : le reste à engager vaut le budget moins le réalisé, jamais moins de zéro — un poste ` +
              `dépassé projette son réalisé. ` +
              (modifiable
                ? `Vous pouvez le remplacer par votre estimation (montant en gris : calcul ; en noir : estimé), et ` +
                  `ajouter ci-dessous une dépense nouvelle, qui s'ajoute au poste choisi. `
                : `L'équipe peut le remplacer par son estimation et ajouter des dépenses prévues. `) +
              `Pour la paie, le réalisé est le versé des bulletins et le reste, ce qu'il reste à verser. Chaque ` +
              `montant réalisé en banque est cliquable et ouvre les écritures qui le composent.`
          : `Note de lecture : un poste dont le réalisé dépasse déjà son budget projette son réalisé — un poste dépassé ` +
              `ne revient pas sous son budget. L'écart est compté favorablement quand la charge projetée reste sous ` +
              `le budget. Chaque montant réalisé est cliquable et ouvre les écritures bancaires qui le composent.`,
      ),
      formulaireDepense,
      insight(
        personnelProjete <= d.previsionnelVote.charges.personnel ? 'positive' : 'warning',
        personnelProjete <= d.previsionnelVote.charges.personnel ? '✦' : '⚠',
        `**Les charges de personnel pèsent ${pourcent(personnelProjete / pr.chargesProjetees, 0)} de la projection.** ` +
          `La masse chargée, ${atterrissage ? 'lue sur les bulletins' : 'calculée'} salarié par salarié, s'établit à ` +
          `${eurosCentimes(personnelProjete)} contre ` +
          `${eurosCentimes(d.previsionnelVote.charges.personnel)} budgétés, soit ` +
          `${eurosSigne(d.previsionnelVote.charges.personnel - personnelProjete)}. ` +
          (atterrissage
            ? `Elle comprend les primes de partage de la valeur et le transport des salariés.`
            : personnelProjete <= d.previsionnelVote.charges.personnel
              ? `Ce gain est acquis : il se lit dans la grille de salaires, pas dans une hypothèse.`
              : `Ce dépassement est structurel : il tient à la grille elle-même, non à un aléa de gestion.`),
      ),
      barreExport(atterrissage ? 'atterrissage-charges' : 'execution-charges', `Détail des charges ${an}`),
    ),
    h('hr', { classe: 'divider' }),
    h(
      'section',
      {},
      enteteSection(3, `De la clôture ${d.cloture.exercice} à la réserve attendue fin ${an}`, 'Chaînage des fonds propres, base de toutes les projections'),
      h(
        'p',
        { classe: 'text-block' },
        d.previsionnelVote.reservePrevueFinExercicePrecedent !== null
          ? `Le prévisionnel voté partait d'une réserve estimée à ` +
            `${eurosCentimes(d.previsionnelVote.reservePrevueFinExercicePrecedent)} au 31 décembre ${d.cloture.exercice}. ` +
            `La comptabilité close fait apparaître ${eurosCentimes(ch.fondsPropresCloture)}. C'est ce montant réel qui ` +
            `sert de point de départ à l'ensemble des trajectoires.`
          : `Aucune réserve n'était projetée au classeur pour le 31 décembre ${d.cloture.exercice}. La comptabilité ` +
            `close fait apparaître ${eurosCentimes(ch.fondsPropresCloture)}, point de départ de toutes les ` +
            `trajectoires.`,
      ),
      tableChainage,
      noteLecture(
        `Note de lecture : le seuil de sécurité ${an} s'établit à ${eurosCentimes(ch.seuilSecurite)}, somme d'un plancher ` +
          `de trésorerie et du coût estimé de rupture du contrat de direction.`,
      ),
      insight(
        'info',
        'ℹ',
        `**Réserve sur la précision des fonds propres :** ${ECART_RACCORDEMENT.mention} La trajectoire présentée devra ` +
          `être confirmée après arbitrage de ce point.`,
      ),
    ),
  );
}
